import type { PerformanceRow, RikuzLineRow } from '@agio/core';

export interface ActiveFund {
  id: string;
  name: string;
}

/**
 * The funds behind the "בתי השקעות פעילים" card for the month on screen.
 *
 * The count comes from the same place as the total beside it. When the month
 * has holdings snapshots (ריכוז), those are the funds. For an earlier month
 * there usually are none — a fund keeps only its current snapshot — and the
 * total then comes from the monthly balances on the returns sheets; so do the
 * funds: every fund with a balance on its returns row in that month. Counting
 * snapshots alone showed "0 active" under a real total for any past month.
 *
 * `null` means "not known yet": the month has no snapshot and its returns
 * rows are not in hand (still loading, or the request failed). The card then
 * shows a dash — never a 0 it cannot vouch for.
 */
export function activeFunds(
  snapshot: Pick<RikuzLineRow, 'fund_id' | 'fund'>[],
  /** The returns rows for the month on screen; null while loading or after a failed request. */
  perf: Pick<PerformanceRow, 'fund_id' | 'fund' | 'date' | 'asset_volume'>[] | null,
  /** The month the total was taken from when there is no snapshot (ISO date). */
  totalDate: string | null,
): ActiveFund[] | null {
  const byId = new Map<string, string>();
  if (snapshot.length > 0) {
    for (const l of snapshot) byId.set(l.fund_id, l.fund);
  } else if (totalDate !== null) {
    if (perf === null) return null;
    const month = totalDate.slice(0, 7);
    for (const p of perf) if (p.date.slice(0, 7) === month && p.asset_volume !== null) byId.set(p.fund_id, p.fund);
  }
  return [...byId].map(([id, name]) => ({ id, name }));
}
